// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Stack,
  Divider,
  Box,
  TextField,
  useTheme,
  InputLabel
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloseOutlined } from '@ant-design/icons';

// ==============================|| LOCAL COMPONENTS & ICONS ||============================== //
import {
  InprogressChipIcon,
  PendingChipIcon,
  CompleteChipIcon
} from 'utils/TaskManagementIcon/taskmanagemeneticon';

// ==============================|| TYPES & INTERFACES ||============================== //
import { CancelModalProps, TaskStatusEnum, getStatusName } from 'types/taskManagement/TaskManagement';
import { useUpdateTaskStatus } from 'api/TaskManagement/taskManagement';

// ==============================|| VALIDATION SCHEMA ||============================== //
const validationSchema = Yup.object({
  reason: Yup.string().trim().required('Cancellation reason is required').max(1000, 'Max 1000 characters allowed')
});

export default function CancelModal({
  open,
  onClose,
  taskTitle,
  taskStatus,
  taskId,
  updatedOn,
  onSuccess,
  path
}: CancelModalProps) {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: { reason: '' },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setSubmitting(true);
      try {
        const res = await useUpdateTaskStatus({
          taskID: Number(taskId),
          taskStatus: TaskStatusEnum.Cancelled,
          reasonToCancel: values.reason,
          updatedOn: updatedOn ?? null
        }, path);
        resetForm();
        onClose();
        if (onSuccess) onSuccess(res);
      } catch {
        // Error is already handled by handleApiError inside useUpdateTaskStatus
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  // ==============================|| STATUS CHIP ||============================== //
  const renderStatusChip = () => {
    const statusStr = String(taskStatus || '').trim().toLowerCase();
    const isDark = theme.palette.mode === 'dark';

    const isPending = statusStr.includes('pending') || taskStatus === TaskStatusEnum.Pending;
    const isCompleted = statusStr.includes('completed') || taskStatus === TaskStatusEnum.Completed;
    const isCancelled = statusStr.includes('cancelled') || taskStatus === TaskStatusEnum.Cancelled;

    const icon = isPending ? <PendingChipIcon /> : isCompleted ? <CompleteChipIcon /> : isCancelled ? <CloseOutlined style={{ fontSize: 13 }} /> : <InprogressChipIcon />;
    const color = isPending ? 'warning.main' : isCompleted ? 'success.main' : isCancelled ? 'error.main' : 'primary.main';
    const bgcolor = isPending
      ? (isDark ? 'rgba(254, 119, 17, 0.15)' : 'warning.lighter')
      : isCompleted
        ? (isDark ? 'rgba(56, 163, 79, 0.15)' : 'success.lighter')
        : isCancelled
          ? (isDark ? 'rgba(255, 77, 79, 0.15)' : 'error.lighter')
          : (isDark ? 'rgba(22, 119, 255, 0.15)' : 'primary.lighter');
    const borderColor = isPending ? 'warning.light' : isCompleted ? 'success.light' : isCancelled ? 'error.light' : 'primary.light';

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, height: 28, px: 1, bgcolor, color, border: '1px solid', borderColor, borderRadius: '6px', whiteSpace: 'nowrap' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', color }}>{icon}</Box>
        <Typography variant="caption" sx={{ color, fontWeight: 500, fontSize: '13px', lineHeight: 1 }}>
          {getStatusName(taskStatus) || taskStatus || 'In Progress'}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleClose();
      }}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: { width: '650px', maxWidth: '95vw', borderRadius: '16px', boxShadow: '0 12px 48px rgba(0, 0, 0, 0.08)', p: 3, overflow: 'hidden' }
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', textAlign: 'left' }}>

        {/* Round Red Cross Icon */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: theme.palette.error.lighter || alpha(theme.palette.error.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CloseOutlined style={{ fontSize: '28px', color: theme.palette.error.main }} />
          </Box>
        </Box>

        {/* Title */}
        <Typography variant="h5" align="center" sx={{ fontWeight: 600, fontSize: '20px', color: theme.palette.text.primary, mb: 1.5 }}>
          Cancel Task
        </Typography>

        {/* Subtitle */}
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '14px', mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Are you sure you want to cancel</Box>
          <Box component="span" title={taskTitle} sx={{ color: theme.palette.text.primary, display: 'inline-block', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            "{taskTitle}"
          </Box>
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>task?</Box>
        </Typography>

        {/* Task Summary Card */}
        <Box sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[50], border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1, px: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="subtitle1" title={taskTitle} sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 130px)' }}>
              {taskTitle}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>{renderStatusChip()}</Box>
          </Stack>
        </Box>

        {/* Cancellation Reason Field */}
        <Box sx={{ mb: 0.5 }}>
          <InputLabel required sx={{ mb: 1 }}>Cancellation Reason</InputLabel>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter Cancellation Reason"
            name="reason"
            value={formik.values.reason}
            inputProps={{ maxLength: 1000 }}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 1000) {
                formik.setFieldValue('reason', val);
              }
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.reason && Boolean(formik.errors.reason)}
            helperText={formik.touched.reason && formik.errors.reason ? formik.errors.reason : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: theme.palette.background.paper,
                '& fieldset': { borderColor: theme.palette.grey[300] },
                '&:hover fieldset': { borderColor: theme.palette.grey[400] },
                '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
              }
            }}
          />
        </Box>

        <Divider sx={{ my: 3, width: '100%', borderColor: theme.palette.divider }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          <Button
            variant="outlined"
            size="large"
            onClick={handleClose}
            disabled={submitting}
            sx={{
              borderRadius: '4px', minWidth: '100px', height: '44px',
              color: theme.palette.text.secondary, borderColor: theme.palette.text.secondary,
              textTransform: 'none', fontWeight: 500, fontSize: '14.5px',
              '&:hover': { borderColor: theme.palette.text.disabled, bgcolor: '#f5f5f5', color: theme.palette.text.secondary }
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={() => formik.handleSubmit()}
            disabled={submitting}
            sx={{
              borderRadius: '4px', minWidth: '130px', height: '44px',
              bgcolor: theme.palette.error.main, color: theme.palette.common.white,
              textTransform: 'none', fontWeight: 400, fontSize: '15px', boxShadow: 'none',
              '&:hover': { bgcolor: theme.palette.error.main, boxShadow: 'none' }
            }}
          >
            {submitting ? 'Confirming...' : 'Confirm Cancel'}
          </Button>
        </Stack>

      </DialogContent>
    </Dialog>
  );
}